import { useEffect, useRef, useState } from 'react'
import { api, fieldErrorsFromDetail } from '../lib/api'
import ConfirmDialog from './ConfirmDialog'
import Segmented from './Segmented'

// The onboarding note's editor — "Edit draft" on the access card. The email
// that carries the principal's two links, as this space will send it:
// subject and body with the placeholders the server renders ({FIRST_NAME},
// {SHARED_BY}, {INTERVIEW_LINK}, {DASHBOARD_LINK} …), a Write | Preview switch that renders the unsaved
// text through the send's own function (POST …/share/preview), and "Reset to
// default", which puts the built-in wording back — nothing is saved until
// Save draft. Saving PATCHes share_subject / share_body; a reset saves them
// as null, so a later change to the default reaches this space too. Reply-To
// (share_reply_to) is the third field: blank means replies reach the admin
// who shares, with hello@capston.ca as the floor. `onSaved` hands back the
// server's space.

// Same shape check as schemas/participants.py EMAIL_RE.
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/

// {DASHBOARD_LINK} is the personal link that opens the space (the server still
// accepts the older {LINK} for the same value); {INTERVIEW_LINK} is the
// principal's own link to try the conversation.
const PLACEHOLDERS = [
  '{FIRST_NAME}',
  '{PRINCIPAL_NAME}',
  '{SHARED_BY}',
  '{CLIENT_NAME}',
  '{ENGAGEMENT}',
  '{PURPOSE}',
  '{INTERVIEW_LINK}',
  '{DASHBOARD_LINK}',
  '{EXPIRY_DATE}',
]

function hasDashboardLink(text) {
  const upper = text.toUpperCase()
  return upper.includes('{DASHBOARD_LINK}') || upper.includes('{LINK}')
}

export default function ShareDraftDialog({ space, onClose, onSaved }) {
  const [subject, setSubject] = useState(space.share_subject ?? '')
  const [body, setBody] = useState(space.share_body ?? '')
  const [replyTo, setReplyTo] = useState(space.share_reply_to ?? '')
  // Reset marks the draft as "back to default" until the text is touched
  // again, so Save can send nulls instead of a copy of the default wording.
  const [useDefault, setUseDefault] = useState(false)
  const [view, setView] = useState('write')
  const [preview, setPreview] = useState(null) // { email_subject, email_body }
  const [previewError, setPreviewError] = useState(null)
  const [fieldErrors, setFieldErrors] = useState({})
  const [error, setError] = useState(null)
  const [saving, setSaving] = useState(false)
  const [resetting, setResetting] = useState(false)
  const bodyRef = useRef(null)

  const replyToChanged = replyTo.trim().toLowerCase() !== (space.share_reply_to ?? '')
  const wordingChanged = useDefault
    ? Boolean(space.share_custom)
    : subject !== space.share_subject || body !== space.share_body
  const dirty = wordingChanged || replyToChanged

  useEffect(() => {
    if (view !== 'preview') return undefined
    let cancelled = false
    setPreview(null)
    setPreviewError(null)
    api(`/api/v1/spaces/${space.id}/share/preview`, {
      method: 'POST',
      body: { email_subject: subject, email_body: body },
    })
      .then((out) => {
        if (!cancelled) setPreview(out)
      })
      .catch((err) => {
        if (!cancelled) setPreviewError(err.detail || 'Could not render the preview.')
      })
    return () => {
      cancelled = true
    }
  }, [view, space.id, subject, body])

  function edit(setter) {
    return (value) => {
      setUseDefault(false)
      setter(value)
    }
  }

  const setSubjectText = edit(setSubject)
  const setBodyText = edit(setBody)

  // Drops the token at the caret of the body textarea (or at the end when the
  // field has not been focused yet), then puts the caret after it.
  function insertPlaceholder(token) {
    const element = bodyRef.current
    const start = element?.selectionStart ?? body.length
    const end = element?.selectionEnd ?? start
    setBodyText(body.slice(0, start) + token + body.slice(end))
    requestAnimationFrame(() => {
      if (!element) return
      element.focus()
      element.setSelectionRange(start + token.length, start + token.length)
    })
  }

  // Puts the built-in wording in the editor, placeholders intact. Nothing is
  // saved until Save draft, which then sends nulls rather than a copy.
  async function handleReset() {
    setError(null)
    setFieldErrors({})
    setResetting(true)
    try {
      const fallback = await api('/api/v1/spaces/share-default')
      setSubject(fallback.email_subject)
      setBody(fallback.email_body)
      setUseDefault(true)
      setView('write')
    } catch (err) {
      setError(err.detail || 'Could not load the default wording.')
    } finally {
      setResetting(false)
    }
  }

  async function handleSave(event) {
    event.preventDefault()
    if (saving || !dirty) return
    const errors = {}
    if (!useDefault) {
      if (!subject.trim()) errors.share_subject = 'Required.'
      if (!body.trim()) errors.share_body = 'Required.'
      else if (!hasDashboardLink(body))
        errors.share_body =
          'The body must include {DASHBOARD_LINK} — it is the principal’s way in.'
    }
    if (replyTo.trim() && !EMAIL_RE.test(replyTo.trim()))
      errors.share_reply_to = 'Enter a valid email address, or leave it blank.'
    setFieldErrors(errors)
    setError(null)
    if (Object.keys(errors).length > 0) return

    setSaving(true)
    try {
      const wording = useDefault
        ? { share_subject: null, share_body: null }
        : { share_subject: subject.trim(), share_body: body.trim() }
      const updated = await api(`/api/v1/spaces/${space.id}`, {
        method: 'PATCH',
        body: { ...wording, share_reply_to: replyTo.trim().toLowerCase() || null },
      })
      onSaved(updated)
    } catch (err) {
      const mapped = fieldErrorsFromDetail(err.detail)
      if (mapped) setFieldErrors(mapped)
      else setError(err.detail || 'Could not save the draft.')
      setSaving(false)
    }
  }

  const inputClass = (name) => `field__input${fieldErrors[name] ? ' field__input--invalid' : ''}`

  return (
    <ConfirmDialog label="Edit the principal’s email" onClose={onClose} wide className="modal--form">
      <form onSubmit={handleSave} noValidate>
        <div className="dialog-head">
          <h2 className="new-space__title">Edit draft</h2>
          <p className="dialog-head__lede">
            The email {space.principal_name} gets when you share this space. It carries two
            links: their own link to try the conversation, and the personal link that opens
            this space. It goes out from Capston, and is sent again with a fresh personal link
            each time you press Share. What you write here is the body; it is sent inside the
            Capston header, with the personal link as a button.
          </p>
        </div>

        <div className="dialog-body">
          {error && (
            <div className="alert alert--error text-body-sm" role="alert">
              {error}
            </div>
          )}

          <div className="share-draft__bar">
            <Segmented
              label="Write or preview"
              value={view}
              onChange={setView}
              options={[
                { value: 'write', label: 'Write' },
                { value: 'preview', label: 'Preview' },
              ]}
            />
            {!useDefault && (space.share_custom || dirty) && (
              <button
                type="button"
                className="text-btn"
                onClick={handleReset}
                disabled={saving || resetting}
              >
                {resetting ? 'Loading…' : 'Reset to default'}
              </button>
            )}
          </div>

          {view === 'write' ? (
            <>
              <div className="field">
                <label className="field__label" htmlFor="share-subject">
                  Subject
                </label>
                <input
                  id="share-subject"
                  type="text"
                  className={inputClass('share_subject')}
                  value={subject}
                  onChange={(event) => setSubjectText(event.target.value)}
                  disabled={saving}
                />
                {fieldErrors.share_subject && (
                  <span className="field__error" role="alert">
                    {fieldErrors.share_subject}
                  </span>
                )}
              </div>
              <div className="field">
                <label className="field__label" htmlFor="share-body">
                  Body
                </label>
                <textarea
                  id="share-body"
                  ref={bodyRef}
                  rows={12}
                  className={inputClass('share_body')}
                  value={body}
                  onChange={(event) => setBodyText(event.target.value)}
                  disabled={saving}
                />
                {fieldErrors.share_body ? (
                  <span className="field__error" role="alert">
                    {fieldErrors.share_body}
                  </span>
                ) : (
                  <span className="field__help">
                    Plain text: each paragraph is one line, and the reader’s mail client wraps
                    it. {'{DASHBOARD_LINK}'} is required; {'{INTERVIEW_LINK}'} is their own link
                    to try the conversation.
                  </span>
                )}
              </div>
              <div className="chips chips--inline">
                <span className="field__help">Insert</span>
                {PLACEHOLDERS.map((token) => (
                  <button
                    key={token}
                    type="button"
                    className="chip-token"
                    onClick={() => insertPlaceholder(token)}
                    disabled={saving}
                  >
                    {token}
                  </button>
                ))}
              </div>
              <div className="field">
                <label className="field__label" htmlFor="share-reply-to">
                  Reply-To <span className="field__label-note">(optional)</span>
                </label>
                <input
                  id="share-reply-to"
                  type="email"
                  className={inputClass('share_reply_to')}
                  value={replyTo}
                  onChange={(event) => setReplyTo(event.target.value)}
                  placeholder="you@capston.ca"
                  disabled={saving}
                />
                {fieldErrors.share_reply_to ? (
                  <span className="field__error" role="alert">
                    {fieldErrors.share_reply_to}
                  </span>
                ) : (
                  <span className="field__help">
                    Where {space.principal_name}’s replies go — to this email and to each
                    completion notice. Blank: the admin who presses Share, with
                    hello@capston.ca as the fallback.
                  </span>
                )}
              </div>
            </>
          ) : (
            <div className="mail-preview" aria-live="polite">
              {previewError ? (
                <p className="text-body-sm" style={{ color: 'var(--color-text-error)' }} role="alert">
                  {previewError}
                </p>
              ) : preview === null ? (
                <p className="text-body-sm" style={{ color: 'var(--color-text-secondary)' }}>
                  Rendering…
                </p>
              ) : (
                <>
                  <p className="mail-preview__label">Subject</p>
                  <p className="mail-preview__subject">{preview.email_subject}</p>
                  <p className="mail-preview__label">Replies go to</p>
                  <p className="mail-preview__subject">
                    {replyTo.trim() || 'the admin who presses Share (hello@capston.ca if none)'}
                  </p>
                  <p className="mail-preview__label">Body</p>
                  <p className="mail-preview__body">{preview.email_body}</p>
                </>
              )}
            </div>
          )}
        </div>

        <div className="dialog-foot dialog-foot--flush">
          <p className="dialog-foot__note">
            {useDefault
              ? 'Saving puts the built-in wording back.'
              : 'Nothing is sent from here — Share does that.'}
          </p>
          <div className="dialog-foot__actions">
            <button
              type="button"
              className="btn btn--secondary btn--outlined"
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn--primary" disabled={!dirty || saving}>
              {saving ? 'Saving…' : 'Save draft'}
            </button>
          </div>
        </div>
      </form>
    </ConfirmDialog>
  )
}
